import { useState } from "react";
import { Eye, EyeSlash, Plus, Trash, ClipboardText } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

export default function ClientWrapSettings({ client, isAdmin, onChanged }) {
  const [newQ, setNewQ] = useState("");
  const [busy, setBusy] = useState(false);
  if (!isAdmin) return null;

  const visible = client.careplan_staff_visible !== false;
  const questions = client.care_questions || [];

  const toggleVisibility = async () => {
    setBusy(true);
    try {
      await api.put(`/clients/${client.id}/careplan-visibility`, { visible: !visible });
      toast.success(visible ? "Care plan is now office-only for this client" : "Care plan visible to caregivers again");
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const saveQuestions = async (next) => {
    setBusy(true);
    try {
      await api.put(`/clients/${client.id}/care-questions`, { questions: next });
      toast.success("Daily wrap-up questions saved");
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="mb-4 rounded-lg border border-stone-200 bg-white p-4 shadow-sm" data-testid="wrap-settings-card">
      <div className="flex items-center gap-2">
        <ClipboardText size={16} className="text-primary" />
        <p className="text-sm font-semibold text-stone-900">Wrap-up notes & caregiver visibility</p>
      </div>
      <div className="mt-3 flex flex-wrap items-center justify-between gap-3 rounded-md border border-stone-100 bg-stone-50 px-3 py-2.5">
        <div>
          <p className="text-sm font-medium text-stone-800">Care plan visible to caregivers</p>
          <p className="text-xs text-stone-500">Caregivers who work with this client can see care plan updates. Turn off to make this care plan office-only.</p>
        </div>
        <Button size="sm" variant="outline" onClick={toggleVisibility} disabled={busy}
          className={visible ? "border-stone-300" : "border-[#C0392B]/50 text-[#C0392B]"} data-testid="careplan-visibility-toggle">
          {visible ? <><Eye size={14} className="mr-1.5" /> Visible — tap to hide</> : <><EyeSlash size={14} className="mr-1.5" /> Office only — tap to show</>}
        </Button>
      </div>
      <div className="mt-3">
        <p className="text-sm font-medium text-stone-800">Client-specific daily questions</p>
        <p className="text-xs text-stone-500">Asked on every wrap-up note for this client, in addition to the rotating and safety questions.</p>
        <div className="mt-2 space-y-1.5">
          {questions.map((q, i) => (
            <div key={i} className="flex items-center gap-2 rounded-md border border-stone-200 px-3 py-1.5" data-testid={`client-wrap-q-${i}`}>
              <p className="flex-1 text-sm text-stone-800">{q}</p>
              <button onClick={() => saveQuestions(questions.filter((_, j) => j !== i))} disabled={busy}
                className="text-stone-400 hover:text-[#C0392B]" data-testid={`client-wrap-q-delete-${i}`}><Trash size={14} /></button>
            </div>
          ))}
          {questions.length === 0 && <p className="text-xs text-stone-400">None set — only the standard rotating + safety questions are asked.</p>}
        </div>
        <div className="mt-2 flex gap-2">
          <Input value={newQ} onChange={(e) => setNewQ(e.target.value)} placeholder="e.g. How was her pain level today?"
            className="border-stone-300" data-testid="client-wrap-new-question" />
          <Button size="sm" onClick={() => { if (!newQ.trim()) return; saveQuestions([...questions, newQ.trim()]); setNewQ(""); }} disabled={busy}
            className="shrink-0 bg-primary text-primary-foreground hover:bg-stone-900" data-testid="client-wrap-add-question">
            <Plus size={14} className="mr-1" /> Add
          </Button>
        </div>
      </div>
    </div>
  );
}
